import { Promotion } from '@/models';
import { AxiosResponse, AxiosError, AxiosInstance } from 'axios';
import moment from 'moment';

import { camelCaseObject } from '@/helpers/formatting';
import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { ApiService } from '../api/api.service';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { PromiseCacher } from '@/helpers';
import { AnalyticsService } from '../analytics/analytics.service';
import { MUTATIONS as ORDER_MUTATIONS} from '@/store/order';

export interface PromotionsService {
  getCustomerPromotions(includeExpired?: boolean): Promise<Promotion[]>;
  getPromotionDetails(code: string): Promise<Promotion | undefined>;

  refreshCustomerPromotions(): Promise<Promotion[]>;
  addPromoCode(promotionCode: string, recaptchaToken?: string): Promise<any>;
}

const PROMOTIONS_BASE_URL = '/promo/v2';

export class AppPromotionsService extends ApiService implements PromotionsService {
  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, private promiseCacher: PromiseCacher, protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  async getCustomerPromotions(includeExpired?: boolean) {
    return this.httpClient
      .get(`${PROMOTIONS_BASE_URL}/customers/promotions`, {
        params: { countryCode: this.store.state.ui.countryProfile.countryCode }
      })
      .then((r: AxiosResponse<object[]>) => {
        const promotions = r.data.map(p => {
          return this.mapPromotionDataToObject(p);
        });

        if (includeExpired) {
          return promotions;
        }

        return promotions.filter(p => p.isValid);
      })
      .catch(e => this.logAndThrowApiError('getCustomerPromotions', e));
  }

  async refreshCustomerPromotions(): Promise<Promotion[]> {
    const user = this.store?.state?.customer?.profile;
    if(!user?.isGuest) {
      return this.promiseCacher.exec({
        name: 'refreshCustomerPromotions',
        forceRefresh: false,
        inFlightOnly: true,
        workFn: async () => {
          try {
            const promotions = await this.getCustomerPromotions();
            this.store.commit(CUSTOMER_MUTATIONS.SET_WALLET_PROMOTIONS, promotions);
            return promotions;
          } catch (e: any) {
            this.logger.error('Something went wrong while refreshing customer promotions', undefined, e);
            throw e;
          }
        },
      });
    } else {
      return [];
    }
  }

  addPromoCode(promotionCode: string, recaptchaToken: string) {
    return this.httpClient.post(`${PROMOTIONS_BASE_URL}/customers/promotions`, {
      promotionCode,
      recaptchaToken,
    });
  }

  async getPromotionDetails(code: string): Promise<Promotion | undefined> {
    if (!code) { 
      return {} as Promotion;
    }
    try {
      const res = await this.httpClient.get(`${PROMOTIONS_BASE_URL}/details/${code}`);
      if (res) {
        return this.mapPromotionDataToObject(res.data);
      }
    } catch (e: any) {
      return this.handlePromotionsApiErr(e);
    }
  }

  handlePromotionsApiErr(e: any): never {
    const err = e as AxiosError;
    const errors = err.response && (err as AxiosError<object[], any>).response?.data;

    if (!(err.response && [400, 401].includes(err.response.status) && errors && errors.length)) {
      throw e;
    }

    const error = camelCaseObject(errors[0]);
    throw error;
  }

  mapPromotionDataToObject(data: object): Promotion {
    const response = camelCaseObject<Promotion>(data);
    this.store.commit(ORDER_MUTATIONS.SET_PROMO_CODE, response.promotionCode, { root: true });
    // We are shaving the 'Z' off the expiration date because Promo/Sparkfly's UTC dates are not really UTC
    // We really just want the date portion until Promo/Sparkfly fixes their dates
    const expirationDateStringUTC = response.expirationDate || '';
    // Only strip a trailing 'Z' if present, so a bare date string isn't corrupted
    const expirationDateStringStandardized = expirationDateStringUTC.endsWith('Z')
      ? expirationDateStringUTC.substring(0, expirationDateStringUTC.length - 1)
      : expirationDateStringUTC;
    // Get around Safari not handling colons in dates
    const expirationDate = moment(expirationDateStringStandardized).format('M/DD/YYYY');

    return {
      ...response,
      expirationDate,
      promotionActivity: response.promotionActivity
        ? {
          ...response.promotionActivity,
          activityDate: moment(response.promotionActivity.activityDate).format('M/DD/YYYY'),
        }
        : null,
    };
  }
}
